import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { NavLink, NavLinkRenderProps } from 'react-router-dom';

import Icon from '@ui/icons/icon';

import styles from './navigation-shortcuts.module.css';
import OptionalComponent from '@ui/optional-component/optional-component';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectFeatureFlags } from '@/store/slices/feature-flags/selectors';
import featureFlagKeyMap from '@/utils/feature-flags/feature-flag-keymap';
import { toggleFeatureFlag } from '@/store/slices/feature-flags';
import { FeatureFlagsEnum } from '@/store/slices/feature-flags/types';
import { isLocalhost } from '@/utils/urls';
import { signOut } from '@/store/slices/user';

const TABS = ['Routing', 'Flags'];
if (isLocalhost) {
  TABS.push('Devs');
}

const renderClass: (props: NavLinkRenderProps) => string | undefined = (props) => {
  if (props.isActive) return styles['active'];
  if (props.isTransitioning) return styles['transitioning'];
  return '';
};

const NavigationShortcuts: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);

  const panelRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 50, y: 50 });
  const [offset, setOffset] = useState({ x: 50, y: 50 });
  const [activeTab, setActiveTab] = useState(TABS[0]);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.altKey && ['å', 'a'].includes(event.key)) {
      // å key becomes "a" when alt is pressed
      setIsOpen((prev) => !prev);
    } else if (event.key === 'Escape') {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  const handleMouseDown = (event: React.MouseEvent) => {
    if (panelRef.current) {
      const rect = panelRef.current.getBoundingClientRect();
      setOffset({ x: event.clientX - rect.left, y: event.clientY - rect.top });
      setIsDragging(true);
      event.preventDefault(); // Prevent default behavior to avoid text selection
    }
  };

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      if (isDragging) {
        setPosition({ x: event.clientX - offset.x, y: event.clientY - offset.y });
      }
    },
    [isDragging, offset]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    } else {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp, isDragging, offset]);

  return (
    <>
      {isOpen && (
        <div
          ref={panelRef}
          className={styles['floating-panel']}
          style={{ left: position.x, top: position.y }}
          onMouseDown={handleMouseDown}
        >
          <div className={styles['header']}>
            <div className="flex fd-row jc-center ai-center">
              {TABS.map((tab) => (
                <div key={tab} className="padding-8">
                  <button
                    className="clear-button flex-column jc-center ai-center"
                    style={{
                      color: 'var(--gray-1200)',
                      ...(tab === activeTab
                        ? { borderBottom: '1px solid var(--gray-1200)' }
                        : { borderBottom: '1px solid transparent' }),
                    }}
                    onClick={() => setActiveTab(tab)}
                  >
                    {tab}
                  </button>
                </div>
              ))}
            </div>
            <button
              className={`radius-full square-30 padding-0 flex-column jc-center ai-center ${styles['close-button']}`}
              onClick={() => setIsOpen(false)}
            >
              <Icon name="close" size={12} />
            </button>
          </div>
          <OptionalComponent display={activeTab === 'Routing'}>
            <ul className={styles['floating-panel-list-outer']}>
              <RoutingList />
            </ul>
          </OptionalComponent>
          <OptionalComponent display={activeTab === 'Flags'}>
            <ul className={`clean-list ${styles['floating-panel-list-outer']}`}>
              <FlagsList />
            </ul>
          </OptionalComponent>
          <OptionalComponent display={activeTab === 'Devs'}>
            <ul className={`clean-list ${styles['floating-panel-list-outer']}`}>
              <DevsList />
            </ul>
          </OptionalComponent>
        </div>
      )}
    </>
  );
};

export default NavigationShortcuts;

const RoutingList = () => {
  return (
    <>
      <li>
        <NavLink className={renderClass} to="/">
          index: <code>/</code>
        </NavLink>
      </li>
      <li>
        <br />
        <NavLink className={renderClass} to="/auth">
          <h4 style={{ display: 'inline-block' }}>Auth:</h4>
        </NavLink>
        <ul className={styles['floating-panel-list-inner']}>
          <li className={styles['list-item']}>
            <NavLink className={renderClass} to="/auth">
              <code>/auth</code>
            </NavLink>
          </li>
          <li className={styles['list-item']}>
            <NavLink className={renderClass} to="/auth/login">
              <code>/auth/login</code>
            </NavLink>
          </li>
        </ul>
      </li>
      <li>
        <NavLink className={renderClass} to="/admin">
          <h4 style={{ display: 'inline-block' }}>Admin:</h4>
        </NavLink>
        <ul className={styles['floating-panel-list-inner']}>
          <li className={styles['list-item']}>
            <NavLink className={renderClass} to="/admin">
              <code>/admin</code>
            </NavLink>
          </li>
          <li className={styles['list-item']}>
            <NavLink className={renderClass} to="/admin/projects">
              <code>/admin/projects</code>
            </NavLink>
          </li>
          <li style={{ marginTop: '10px' }}>
            <h6>PROJECT OVERVIEW:</h6>
          </li>
          <li className={styles['list-item']}>
            <NavLink className={renderClass} to="/admin/projects/qwerty-123456-qwerty-123456/overview/sessions">
              <code>/admin/projects/:projectUuid/overview/sessions</code>
            </NavLink>
          </li>
          <li className={styles['list-item']}>
            <NavLink className={renderClass} to="/admin/projects/qwerty-123456-qwerty-123456/overview/registrations">
              <code>/admin/projects/:projectUuid/overview/registrations</code>
            </NavLink>
          </li>
          <li className={styles['list-item']}>
            <NavLink className={renderClass} to="/admin/projects/qwerty-123456-qwerty-123456/overview/audiences">
              <code>/admin/projects/:projectUuid/overview/audiences</code>
            </NavLink>
          </li>
          <li className={styles['list-item']}>
            <NavLink className={renderClass} to="/admin/projects/qwerty-123456-qwerty-123456/overview/integrations">
              <code>/admin/projects/:projectUuid/overview/integrations</code>
            </NavLink>
          </li>
          <li className={styles['list-item']}>
            <NavLink className={renderClass} to="/admin/projects/qwerty-123456-qwerty-123456/overview/leaderboard">
              <code>/admin/projects/:projectUuid/overview/leaderboard</code>
            </NavLink>
          </li>
          <li style={{ marginTop: '10px' }}>
            <h6>PROJECT EDITOR:</h6>
          </li>
          <li className={styles['list-item']}>
            <NavLink className={renderClass} to="/admin/projects/qwerty-123456-qwerty-123456/edit">
              <code>/admin/projects/:projectUuid/edit</code>
            </NavLink>
          </li>
          <li className={styles['list-item']}>
            <NavLink className={renderClass} to="/admin/projects/qwerty-123456-qwerty-123456/edit/route-without-footer">
              <code>/admin/projects/:projectUuid/edit/route-without-footer</code>
            </NavLink>
          </li>
          <li className={styles['list-item']}>
            <NavLink className={renderClass} to="/admin/projects/qwerty-123456-qwerty-123456/edit/registration">
              <code>/admin/projects/:projectUuid/edit/registration</code>
            </NavLink>
          </li>
        </ul>
      </li>

      <li>
        <NavLink className={renderClass} to="/choose">
          <h4 style={{ display: 'inline-block' }}>Choose:</h4>
        </NavLink>
        <ul className={styles['floating-panel-list-inner']}>
          <li className={styles['list-item']}>
            <NavLink className={renderClass} to="/choose">
              <code>/choose</code>
            </NavLink>
          </li>
          <ul>
            <li className={styles['list-item']}>
              <h4 style={{ display: 'inline-block' }}>Wizard:</h4>
            </li>
            <li className={styles['list-item']}>
              <NavLink className={renderClass} to="/choose/project-details/wizard/12345">
                <code>/choose/project-details/wizard/12345</code>
              </NavLink>
              <ul className={styles['floating-panel-list-inner']}>
                <li className={styles['list-item']}>
                  <NavLink className={renderClass} to="/choose/project-details/wizard/12345/webinar-and-allhands">
                    <code>/choose/project-details/wizard/12345/webinar-and-allhands</code>
                  </NavLink>
                </li>
                <li className={styles['list-item']}>
                  <NavLink className={renderClass} to="/choose/project-details/wizard/12345/events-and-others">
                    <code>/choose/project-details/wizard/12345/events-and-others</code>
                  </NavLink>
                </li>
                <li className={styles['list-item']}>
                  <NavLink className={renderClass} to="/choose/project-details/wizard/12345/on-demand">
                    <code>/choose/project-details/wizard/12345/on-demand</code>
                  </NavLink>
                </li>
              </ul>
            </li>
          </ul>
          <ul>
            <li className={styles['list-item']}>
              <h4 style={{ display: 'inline-block' }}>Manual:</h4>
            </li>
            <li className={styles['list-item']}>
              <NavLink className={renderClass} to="/project/new/12345">
                <code>/project/new/12345</code>
              </NavLink>
              <ul className={styles['floating-panel-list-inner']}></ul>
            </li>
          </ul>
        </ul>
      </li>

      <li>
        <NavLink className={renderClass} to="/presenter">
          <h4 style={{ display: 'inline-block' }}>Presenter:</h4>
        </NavLink>
        <ul className={styles['floating-panel-list-inner']}>
          <li className={styles['list-item']}>
            <NavLink className={renderClass} to="/presenter">
              <code>/presenter</code>
            </NavLink>
          </li>
        </ul>
      </li>
    </>
  );
};

const FlagsList = () => {
  const flags = useAppSelector(selectFeatureFlags);

  const dispatch = useAppDispatch();

  const inAppUse = useMemo(() => {
    const inAppUse: [string, string, boolean][] = [];
    Object.entries(featureFlagKeyMap).forEach((flag) => {
      const keyUsed = flag[0];
      const flagName = flag[1];
      if (flagName in flags) {
        inAppUse.push([keyUsed, flagName, !!flags[flagName]]);
      }
    });

    return inAppUse;
  }, [flags]);

  return (
    <>
      <li>
        <br />
        <h3>Flags used in this app</h3>
        <div>(option/alt) + shift + (digit/key)</div>
        <br />
      </li>
      {inAppUse.map((flag) => {
        const keyUsed = flag[0];
        const name = flag[1] as FeatureFlagsEnum;
        const value = flag[2];
        return (
          <li key={name} className="margin-b-10">
            <code>
              {keyUsed}: {name}:
            </code>{' '}
            <button onClick={() => dispatch(toggleFeatureFlag(name))} className="padding-5">
              {value.toString()}
            </button>
          </li>
        );
      })}
    </>
  );
};

const DevsList = () => {
  const dispatch = useAppDispatch();

  return (
    <>
      <li>
        <button
          onClick={() => {
            dispatch(signOut());
          }}
        >
          Signout
        </button>
      </li>
    </>
  );
};
